import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PublicConfig {
  id: number;
  show_logo: boolean;
  logo_url: string;
  show_welcome_message: boolean;
  show_exhibit_access: boolean;
  exhibit_access_buttons: {
    enterNumber: boolean;
    scanQR: boolean;
    favorites: boolean;
    browseTours: boolean;
  };
  show_visitor_info: boolean;
  show_background_shapes: boolean;
  typography_source: 'google' | 'custom';
  typography_google_font: string;
  typography_custom_font_url: string | null;
  use_custom_welcome_message: boolean;
  welcome_message_custom: Record<string, string>;
  element_order: string[];
  button_labels: Record<string, Record<string, string>>;
  show_home_image: boolean;
  home_image_settings: {
    imageUrl: string;
    altText: string;
    caption: string;
    size: 'small' | 'medium' | 'large';
  };
  created_at: string;
  updated_at: string;
}

// Default configuration used as fallback
const DEFAULT_CONFIG: PublicConfig = {
  id: 1,
  show_logo: true,
  logo_url: "/lovable-uploads/727626ab-d59a-4f99-aed6-fe8a671929b5.png",
  show_welcome_message: true,
  show_exhibit_access: true,
  exhibit_access_buttons: {
    enterNumber: true,
    scanQR: true,
    favorites: true,
    browseTours: true
  },
  show_visitor_info: true,
  show_background_shapes: true,
  typography_source: 'google',
  typography_google_font: 'Inter',
  typography_custom_font_url: null,
  use_custom_welcome_message: false,
  welcome_message_custom: {},
  element_order: ['logo', 'welcomeMessage', 'exhibitAccess', 'visitorInfo', 'homeImage'],
  button_labels: {},
  show_home_image: false,
  home_image_settings: {
    imageUrl: '',
    altText: '',
    caption: '',
    size: 'medium'
  },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

export function usePublicConfig() {
  const [config, setConfig] = useState<PublicConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('public_config')
        .select('*')
        .single();

      if (error) {
        console.error('Error loading public config:', error);
        // Silently fall back to default config
        setConfig(DEFAULT_CONFIG);
        return;
      }

      setConfig({
        ...data,
        exhibit_access_buttons: typeof data.exhibit_access_buttons === 'object' 
          ? data.exhibit_access_buttons as any
          : DEFAULT_CONFIG.exhibit_access_buttons,
        welcome_message_custom: typeof data.welcome_message_custom === 'object'
          ? data.welcome_message_custom as any
          : DEFAULT_CONFIG.welcome_message_custom,
        button_labels: typeof data.button_labels === 'object'
          ? data.button_labels as any
          : DEFAULT_CONFIG.button_labels,
        home_image_settings: typeof data.home_image_settings === 'object'
          ? data.home_image_settings as any
          : DEFAULT_CONFIG.home_image_settings
      } as PublicConfig);
    } catch (error) {
      console.error('Error loading public config:', error);
      // Silently fall back to default config
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const reloadConfig = async () => {
    await loadConfig();
  };

  return {
    config,
    loading,
    reloadConfig
  };
}